import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Loader } from 'lucide-react';

// --- IMPORTS DES PAGES ---
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import PricingPage from './pages/PricingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import MentionsLegales from './pages/MentionsLegales';
import HelpPage from './pages/HelpPage';
import OnboardingPage from './pages/OnboardingPage';

// Configuration API
// Configuration API
// FORCE RELATIVE PATH pour éviter tout problème d'environnement ou de cache
const API_URL = '/api';

export default function App() {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  // 🎨 THÈME : 'classic' par défaut (Vert/Sombre)
  const [theme, setTheme] = useState(localStorage.getItem('app_theme') || 'classic');

  const toggleTheme = () => {
    const newTheme = theme === 'modern' ? 'classic' : 'modern';
    setTheme(newTheme);
    localStorage.setItem('app_theme', newTheme);
    window.dispatchEvent(new Event("storage"));
  };

  // --- LOGIQUE ABONNEMENT ---

  const calculateDaysLeft = (trialEndDate) => {
    if (!trialEndDate) return null;
    const now = new Date();
    const end = new Date(trialEndDate);
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const loadUserSubscription = useCallback(async (userId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_URL}/usersubscription/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) return;

      const data = await response.json();

      if (data.subscription) {
        setSubscription(data.subscription);
        if (data.subscription.status === 'trial') {
          setTrialDaysLeft(calculateDaysLeft(data.subscription.trial_end));
        }
      }
    } catch (err) {
      console.error('Erreur chargement abonnement:', err);
    }
  }, []);

  // Vérification de l'auth au chargement de l'application
  useEffect(() => {
    const initAuth = async () => {
      const savedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      if (savedUser && token) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          await loadUserSubscription(userData.id);
        } catch (err) {
          console.error("Erreur parsing user", err);
          localStorage.clear();
        }
      }
      setIsLoadingUser(false);
    };
    initAuth();
  }, [loadUserSubscription]);

  const logout = () => {
    setUser(null);
    setSubscription(null);
    setTrialDaysLeft(null);
    localStorage.clear();
    window.location.href = "/";
  };

  if (isLoadingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  // Props partagées
  const commonProps = { user, API_URL, currentTheme: theme, onToggleTheme: toggleTheme };

  return (
    <Router>
      <Routes>
        {/* 1. Landing Page (Publique) */}
        <Route
          path="/"
          element={
            <LandingPage
              setUser={setUser}
              loadSub={loadUserSubscription}
              {...commonProps}
            />
          }
        />

        {/* 2. Dashboard (Protégé) */}
        <Route
          path="/dashboard"
          element={
            user ? (
              <Dashboard
                subscription={subscription}
                trialDaysLeft={trialDaysLeft}
                logout={logout}
                setUser={setUser}
                {...commonProps}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* 3. Onboarding (Nouveau) */}
        <Route
          path="/onboarding"
          element={
            user ? (
              <OnboardingPage
                {...commonProps}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* 4. Pages Annexes */}
        <Route
          path="/pricing"
          element={
            <PricingPage
              trialDaysLeft={trialDaysLeft}
              {...commonProps}
            />
          }
        />

        <Route
          path="/forgot-password"
          element={<ForgotPasswordPage {...commonProps} />}
        />

        <Route
          path="/mentions-legales"
          element={<MentionsLegales {...commonProps} />}
        />

        <Route
          path="/help"
          element={<HelpPage {...commonProps} />}
        />

        {/* 5. Fallback 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}